import { type ReactNode } from "react";

import { BTDropdownButton, BTOverflow, BTText } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import type { SpecificationResponse } from "@btnet/services-btnet/handlers/models/specificationResponse";
import { SpecificationStatus } from "@btnet/services-btnet/handlers/models/specificationStatus";
import { cn } from "@btnet/utilities-core";

import {
    BdsIconArrowLeft,
    BdsIconArrowUUpLeft,
    BdsIconCatalog,
    BdsIconClockCounterClockwise,
    BdsIconComments,
    BdsIconCommentsFilled,
    BdsIconEye,
    BdsIconPaperPlaneRight,
    BdsIconPencilSimple,
    BdsIconPrinter,
    BdsIconShare,
    BdsIconTrash,
    BdsIconX,
} from "@buildertrend/components";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTItemHeader } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader";
import { CommentsButtonForPage } from "commonComponents/entity/comment/CommentComponentButtons";
import { type IHasDiscussionContainer } from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import "./SpecificationViewerHeader.less";

import { SpecificationInfoButton } from "./SpecificationInfoButton";

export type SpecificationViewerDismissMode = "none" | "back" | "close";

/**
 * A header action the user invoked. The header only renders and reports these — the viewer owns
 * what each one does (confirms, API calls, toasts, panel state).
 */
export type SpecificationHeaderAction =
    | "delete"
    | "print"
    | "unpublish"
    | "viewingPermissions"
    | "saveToCatalog"
    | "versionHistory"
    | "copyShareLink"
    | "createQrCode"
    | "comments"
    | "publish"
    | "edit";

/** Full width means there's no sibling list to dismiss into, so the action is back navigation. */
export function getSpecificationViewerDismissMode({
    canDismiss,
    isFullWidth,
}: {
    canDismiss: boolean;
    isFullWidth: boolean;
}): SpecificationViewerDismissMode {
    if (!canDismiss) return "none";
    return isFullWidth ? "back" : "close";
}

interface ISpecificationViewerHeaderProps {
    /** Compact panel layout (BTItemHeader wrapped for a split-view detail pane) vs. the full-page book view. Only changes which testids are stamped — both render the same markup. */
    isPanelMode: boolean;
    dismissMode: SpecificationViewerDismissMode;
    onDismiss: () => void;
    /** Hides the title row (Save/Cancel/Publish take that space) and makes dismiss cancel the edit. */
    isEditing: boolean;
    /** A request is in flight: disables the dismiss button while editing, the action row otherwise. */
    isActionInFlight: boolean;
    title: string;
    /** Whether this viewer's config surfaces the Published/Draft status badge (e.g. `panelConfig.status`, `!isExternalMode`). */
    canShowStatus: boolean;
    /**
     * Edit-mode actions (Save/Publish), which stay with the viewer since they're driven by its form
     * state. When set, these replace the view-mode action row.
     */
    editActions: ReactNode;
    /** View-mode action visibility. Omit to render no action row at all (external/share mode). */
    actionVisibility?: ISpecificationHeaderActionVisibility;
    /** Invoked when the user picks a view-mode action; the viewer performs it. */
    onAction: (action: SpecificationHeaderAction) => void;
    /** Drives the status badge, the Info popover's audit details, and the comments button. */
    specification: SpecificationResponse;
    /** The spec's discussion, when loaded — supplies comment count and unread state. */
    discussionEntity: IHasDiscussionContainer | undefined;
    isCommentsOpen: boolean;
    isVersionHistoryOpen: boolean;
}

/** Which view-mode actions the header renders. Mirrors useSpecificationActionVisibility's output. */
export interface ISpecificationHeaderActionVisibility {
    showDelete: boolean;
    showPrint: boolean;
    showUnpublish: boolean;
    showViewingPermissions: boolean;
    showSaveToCatalog: boolean;
    showShare: boolean;
    showEdit: boolean;
    showActionsMenu: boolean;
    showInfo: boolean;
    showVersionHistory: boolean;
    showComments: boolean;
    showPublish: boolean;
}

/**
 * The single header shared by every SpecificationViewer render path (panel mode and book view).
 *
 * Two rows, always: back arrow + trailing actions on top, title + status badge below, so the
 * status badge never competes with the actions for space and can't truncate.
 */
export function SpecificationViewerHeader({
    isPanelMode,
    dismissMode,
    onDismiss,
    isEditing,
    isActionInFlight,
    title,
    canShowStatus,
    editActions,
    actionVisibility,
    onAction,
    specification,
    discussionEntity,
    isCommentsOpen,
    isVersionHistoryOpen,
}: Readonly<ISpecificationViewerHeaderProps>) {
    const isPublished = specification.status === SpecificationStatus.Published;
    const dismissButton =
        dismissMode === "none" ? null : (
            <BTButton
                onClick={onDismiss}
                data-testid={
                    isPanelMode
                        ? "specification-viewer-panel-close-button"
                        : "specification-viewer-close-button"
                }
                icon={dismissMode === "back" ? <BdsIconArrowLeft /> : <BdsIconX />}
                type="tertiary"
                hotkey={isEditing ? "close" : undefined}
                disabled={isEditing ? isActionInFlight : undefined}
                aria-label={dismissMode === "back" ? "Back" : "Close"}
            />
        );

    const actions = isEditing ? (
        editActions
    ) : actionVisibility ? (
        <SpecificationHeaderActions
            visibility={actionVisibility}
            onAction={onAction}
            isActionInFlight={isActionInFlight}
            specification={specification}
            discussionEntity={discussionEntity}
            isCommentsOpen={isCommentsOpen}
            isVersionHistoryOpen={isVersionHistoryOpen}
        />
    ) : null;

    return (
        <BTItemHeader
            testid="specification-viewer-header"
            className={cn("viewer-header", { "viewer-header--panel": isPanelMode })}
            ariaLabel="spec-viewer-header"
        >
            <div className="viewer-header--top-row">
                {dismissMode === "back" && dismissButton}
                <div className="flex items-center gap-2 ml-auto min-w-0 flex-1">
                    {actions}
                    {dismissMode === "close" && dismissButton}
                </div>
            </div>
            {!isEditing && (
                <div className="viewer-header--title-group">
                    <BTText
                        tag="h2"
                        size="distinct-lg"
                        className="viewer-header--title"
                        testid={isPanelMode ? "specification-viewer-panel-title" : undefined}
                        text={title}
                    />
                    {canShowStatus && (
                        <StatusTagDisplay
                            statusType={isPublished ? "success" : ""}
                            statusText={isPublished ? "Published" : "Draft"}
                            data-testid={
                                isPanelMode
                                    ? "specification-viewer-panel-status"
                                    : "specification-viewer-status"
                            }
                        />
                    )}
                </div>
            )}
        </BTItemHeader>
    );
}

/**
 * The view-mode action row. One BTOverflow for every action, mirroring TaskActionBar. Keep the
 * pinned set small — pinned items never collapse, so each one raises the width floor before the
 * row starts overlapping the back arrow.
 */
function SpecificationHeaderActions({
    visibility,
    onAction,
    isActionInFlight,
    specification,
    discussionEntity,
    isCommentsOpen,
    isVersionHistoryOpen,
}: Readonly<{
    visibility: ISpecificationHeaderActionVisibility;
    onAction: (action: SpecificationHeaderAction) => void;
    /** A request is in flight — disables destructive/mutating actions. */
    isActionInFlight: boolean;
    specification: SpecificationResponse;
    discussionEntity: IHasDiscussionContainer | undefined;
    isCommentsOpen: boolean;
    isVersionHistoryOpen: boolean;
}>) {
    const hasComments = (discussionEntity?.discussions.comments.comments.length ?? 0) > 0;
    const oldestUnreadCommentId = discussionEntity?.discussions.oldestUnreadCommentId ?? null;
    const commentsIcon = hasComments ? <BdsIconCommentsFilled /> : <BdsIconComments />;

    // Info and Share always render inline, so listing them would show an empty "More".
    const overflowIds = [
        visibility.showDelete && "specification-viewer-delete",
        visibility.showPrint && "specification-viewer-print",
        visibility.showUnpublish && "specification-viewer-unpublish",
        visibility.showViewingPermissions && "specification-viewer-viewing-permissions",
        visibility.showSaveToCatalog && "specification-viewer-save-to-catalog",
        visibility.showVersionHistory && "specification-viewer-version-history-button",
        visibility.showComments && "specification-viewer-comments",
        visibility.showPublish && "specification-viewer-publish",
        visibility.showEdit && "specification-viewer-edit-button",
    ].filter((id): id is string => !!id);

    return (
        <BTOverflow
            testid="specification-viewer-actions"
            style={{ justifyContent: "flex-end" }}
            overflowDirection="start"
        >
            <BTOverflow.Menu testid="specification-viewer-actions-menu" ids={overflowIds} />
            {visibility.showActionsMenu && (
                <>
                    {visibility.showDelete && (
                        <BTOverflow.Item
                            id="specification-viewer-delete"
                            overflowContent={
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconTrash />}
                                    data-testid="specification-viewer-delete-overflow"
                                    onClick={() => onAction("delete")}
                                    disabled={isActionInFlight}
                                >
                                    Delete
                                </BTButton>
                            }
                        >
                            <BTPopover content="Delete" placement="top">
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconTrash />}
                                    data-testid="specification-viewer-delete"
                                    onClick={() => onAction("delete")}
                                    disabled={isActionInFlight}
                                    hotkey="delete"
                                    aria-label="Delete"
                                />
                            </BTPopover>
                        </BTOverflow.Item>
                    )}
                    {visibility.showPrint && (
                        <BTOverflow.Item
                            id="specification-viewer-print"
                            overflowContent={
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconPrinter />}
                                    data-testid="specification-viewer-print-overflow"
                                    onClick={() => onAction("print")}
                                    disabled={isActionInFlight}
                                >
                                    Print
                                </BTButton>
                            }
                        >
                            <BTPopover content="Print" placement="top">
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconPrinter />}
                                    data-testid="specification-viewer-print"
                                    onClick={() => onAction("print")}
                                    disabled={isActionInFlight}
                                    hotkey="print"
                                    aria-label="Print"
                                />
                            </BTPopover>
                        </BTOverflow.Item>
                    )}
                    {visibility.showUnpublish && (
                        <BTOverflow.Item
                            id="specification-viewer-unpublish"
                            overflowContent={
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconArrowUUpLeft />}
                                    data-testid="specification-viewer-unpublish-overflow"
                                    onClick={() => onAction("unpublish")}
                                    disabled={isActionInFlight}
                                >
                                    Move to draft
                                </BTButton>
                            }
                        >
                            <BTPopover content="Move to draft" placement="top">
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconArrowUUpLeft />}
                                    data-testid="specification-viewer-unpublish"
                                    onClick={() => onAction("unpublish")}
                                    disabled={isActionInFlight}
                                    aria-label="Move to draft"
                                />
                            </BTPopover>
                        </BTOverflow.Item>
                    )}
                    {visibility.showViewingPermissions && (
                        <BTOverflow.Item
                            id="specification-viewer-viewing-permissions"
                            overflowContent={
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconEye />}
                                    data-testid="specification-viewer-viewing-permissions-overflow"
                                    onClick={() => onAction("viewingPermissions")}
                                    disabled={isActionInFlight}
                                >
                                    Viewing permissions
                                </BTButton>
                            }
                        >
                            <BTPopover content="Viewing permissions" placement="top">
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconEye />}
                                    data-testid="specification-viewer-viewing-permissions"
                                    onClick={() => onAction("viewingPermissions")}
                                    disabled={isActionInFlight}
                                    aria-label="Viewing permissions"
                                />
                            </BTPopover>
                        </BTOverflow.Item>
                    )}
                    {visibility.showSaveToCatalog && (
                        <BTOverflow.Item
                            id="specification-viewer-save-to-catalog"
                            overflowContent={
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconCatalog />}
                                    data-testid="specification-viewer-save-to-catalog-overflow"
                                    onClick={() => onAction("saveToCatalog")}
                                    disabled={isActionInFlight}
                                >
                                    Save to catalog
                                </BTButton>
                            }
                        >
                            <BTPopover content="Save to catalog" placement="top">
                                <BTButton
                                    type="tertiary"
                                    icon={<BdsIconCatalog />}
                                    data-testid="specification-viewer-save-to-catalog"
                                    onClick={() => onAction("saveToCatalog")}
                                    disabled={isActionInFlight}
                                    aria-label="Save to catalog"
                                />
                            </BTPopover>
                        </BTOverflow.Item>
                    )}
                </>
            )}
            {visibility.showInfo && (
                <SpecificationInfoButton
                    id="specification-info"
                    createdBy={specification.createdBy ?? undefined}
                    createdDate={specification.createdDate}
                    lastUpdatedBy={specification.lastUpdatedBy ?? undefined}
                    lastUpdatedDate={specification.lastUpdatedDate}
                />
            )}
            {visibility.showVersionHistory && (
                <BTOverflow.Item
                    id="specification-viewer-version-history-button"
                    overflowContent={
                        <BTButton
                            type="tertiary"
                            icon={<BdsIconClockCounterClockwise />}
                            data-testid="specification-viewer-version-history-button-overflow"
                            onClick={() => onAction("versionHistory")}
                            selected={isVersionHistoryOpen}
                        >
                            Version history
                        </BTButton>
                    }
                >
                    <BTPopover content="Version history" placement="top">
                        <BTButton
                            onClick={() => onAction("versionHistory")}
                            data-testid="specification-viewer-version-history-button"
                            icon={<BdsIconClockCounterClockwise />}
                            type="tertiary"
                            selected={isVersionHistoryOpen}
                            aria-label="Version history"
                        />
                    </BTPopover>
                </BTOverflow.Item>
            )}
            {visibility.showShare && (
                <BTOverflow.Item id="specification-viewer-share">
                    <BTPopover content="Share" placement="top">
                        <BTDropdownButton
                            data-testid="specification-viewer-share"
                            type="tertiary"
                            showCaret={false}
                            buttonProps={{
                                icon: <BdsIconShare />,
                                "aria-label": "Share specification dropdown",
                            }}
                            menuItems={[
                                {
                                    "data-testid": "specification-viewer-copy-share-link",
                                    content: "Copy share link",
                                    onClick: () => onAction("copyShareLink"),
                                    "aria-label": "Copy share link",
                                },
                                {
                                    "data-testid": "specification-viewer-create-qr-code",
                                    content: "Create QR code",
                                    onClick: () => onAction("createQrCode"),
                                    "aria-label": "Create QR code",
                                },
                            ]}
                        />
                    </BTPopover>
                </BTOverflow.Item>
            )}
            {visibility.showComments && (
                <BTOverflow.Item
                    id="specification-viewer-comments"
                    pinned
                    overflowContent={
                        <BTButton
                            type="tertiary"
                            icon={commentsIcon}
                            data-testid="specification-viewer-comments-overflow"
                            onClick={() => onAction("comments")}
                            selected={isCommentsOpen}
                        >
                            Comments
                        </BTButton>
                    }
                >
                    {/* CommentsButtonForPage's own popover only surfaces the
                        unavailable-before-save reason, so it stays silent on a saved spec —
                        wrap it to match the other header icons' hover labels. */}
                    <BTPopover content="Comments" placement="top">
                        <CommentsButtonForPage
                            id="specification-comments"
                            entityId={specification.id}
                            lastUnreadCommentId={oldestUnreadCommentId}
                            hasUnreadComments={!!oldestUnreadCommentId}
                            isCommentDrawerOpen={isCommentsOpen}
                            onClick={() => onAction("comments")}
                            icon={commentsIcon}
                            text="Comments"
                            hasComments={hasComments}
                            useNewBTButton
                        />
                    </BTPopover>
                </BTOverflow.Item>
            )}
            {visibility.showPublish && (
                <BTOverflow.Item
                    id="specification-viewer-publish"
                    pinned
                    overflowContent={
                        <BTButton
                            type="tertiary"
                            icon={<BdsIconPaperPlaneRight />}
                            data-testid="specification-viewer-publish-overflow"
                            onClick={() => onAction("publish")}
                            disabled={isActionInFlight}
                        >
                            Publish
                        </BTButton>
                    }
                >
                    <BTPopover content="Publish" placement="top">
                        <BTButton
                            type="tertiary"
                            icon={<BdsIconPaperPlaneRight />}
                            data-testid="specification-viewer-publish"
                            onClick={() => onAction("publish")}
                            disabled={isActionInFlight}
                            aria-label="Publish"
                        />
                    </BTPopover>
                </BTOverflow.Item>
            )}
            {visibility.showEdit && (
                <BTOverflow.Item
                    id="specification-viewer-edit-button"
                    pinned
                    overflowContent={
                        <BTButton
                            type="tertiary"
                            icon={<BdsIconPencilSimple />}
                            data-testid="specification-viewer-edit-button-overflow"
                            onClick={() => onAction("edit")}
                            disabled={isActionInFlight}
                        >
                            Edit
                        </BTButton>
                    }
                >
                    <BTPopover content="Edit" placement="top">
                        <BTButton
                            onClick={() => onAction("edit")}
                            data-testid="specification-viewer-edit-button"
                            icon={<BdsIconPencilSimple />}
                            type="tertiary"
                            disabled={isActionInFlight}
                            aria-label="Edit"
                        />
                    </BTPopover>
                </BTOverflow.Item>
            )}
        </BTOverflow>
    );
}
